import { useCallback, useEffect, useRef, useState } from "preact/hooks";

/**
 * Initial chunk rendered on first paint. Sized so a portrait phone shows
 * ~5–8 cards above the fold and another ~20+ cards below — enough scroll
 * runway that the N-3 sentinel doesn't fire on the first interaction.
 */
export const INITIAL_VISIBLE = 30;
/**
 * Articles appended per sentinel firing. Picked so a normal flick fills
 * the viewport without obvious "loading" beats; the manifest caps at ~200
 * so the user reaches the end in ~9 chunks.
 */
export const CHUNK_SIZE = 20;
/**
 * The sentinel is attached to the (N - TRIGGER_OFFSET)-th card. N-3 keeps
 * the user well clear of the bottom when the next chunk lands — a sentinel
 * on the last card would only fire after the user has already hit the end.
 */
export const TRIGGER_OFFSET = 3;

export interface EndlessScrollHandle<T extends Element = HTMLElement> {
  /** How many items the consumer should slice off the top of its list. */
  visibleCount: number;
  /** Ref to attach to the (N-TRIGGER_OFFSET)-th rendered element. */
  triggerRef: { current: T | null };
  /** Index in the rendered (sliced) array that should carry the sentinel
   * ref. Returns -1 when the list is fully visible OR when there are too
   * few visible items to safely place the sentinel (visibleCount ≤
   * TRIGGER_OFFSET); consumers can use that to skip attaching the ref.
   * In the latter case `loadMore()` is the escape hatch. */
  triggerIndex: number;
  /** True when every item is now visible. Useful for rendering a
   * "you're caught up" footer or to gate other UI. */
  exhausted: boolean;
  /** Imperative "show me more" handle. Bumps visibleCount by CHUNK_SIZE
   * (capped at total), regardless of whether the sentinel is in the
   * viewport. Used by consumers when `triggerIndex === -1` but
   * `exhausted === false` (the short-window case), or to wire a manual
   * "Load more" button as a fallback for environments without
   * IntersectionObserver. No-op when already exhausted. */
  loadMore: () => void;
  /** True from the moment the sentinel fires (or `loadMore()` is called)
   * until the next animation frame. Consumers can render `aria-busy` on
   * the list or a small loading indicator. Mirrors the rAF-debounce
   * window that protects against fast-scroll cascade. */
  loadingMore: boolean;
}

/**
 * Endless-scroll state for a finite, client-side list (manifest of ~200
 * articles, in our case). Renders the first `INITIAL_VISIBLE` items, then
 * appends `CHUNK_SIZE` more each time the sentinel — attached to the
 * (N-`TRIGGER_OFFSET`)-th rendered card — scrolls within the
 * `rootMargin` of the bottom of the viewport.
 *
 * Why client-side and not virtualized: the full manifest is already in
 * memory (the SW caches it), each card is cheap (no per-card network),
 * and the absolute cap is ~200 articles. Virtualization buys nothing and
 * costs measurable layout complexity (sticky headers, image lazy-load
 * interactions, accessibility).
 *
 * Behavior across list-length changes:
 * - `total` shrinks (Hide / filter): `visibleCount` clamps to `total`.
 * - `total` grows (manifest refresh): visibleCount stays — the user
 *   keeps their scroll position; fresh items past the current cap become
 *   reachable by scrolling.
 * - Interest-vector re-rank rearranges items but doesn't change `total`,
 *   so visibleCount is unaffected — the user's "how far down" feel is
 *   preserved.
 *
 * Cascade protection: each chunk bump rebinds the observer, and a real-
 * browser IO fires its callback once initially if the new sentinel
 * already satisfies the threshold + rootMargin (spec calls this the
 * "initial observation"). During a hard flick the new (N-3)-th card can
 * already be inside the margin, which would synchronously cascade
 * through every remaining chunk. We rAF-gate `setVisibleCount` so at
 * most one chunk lands per animation frame — bounded, perceptible
 * progress instead of "200 cards added in one tick."
 *
 * The IntersectionObserver re-binds on every visibleCount change because
 * the sentinel element identity moves to a new card; observe/disconnect
 * inside the effect handles that.
 */
export function useEndlessScroll<T extends Element = HTMLElement>(
  total: number,
): EndlessScrollHandle<T> {
  const [visibleCount, setVisibleCount] = useState(
    Math.min(INITIAL_VISIBLE, total),
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const triggerRef = useRef<T | null>(null);
  // rAF debounce gate. Set true the moment a chunk bump is queued;
  // cleared on the next animation frame. While true, further bump
  // requests no-op — that's what stops a fast-scroll cascade from
  // committing the entire manifest in a single frame.
  const pendingFrameRef = useRef<number | null>(null);

  // First-load catch-up. The `useState` initializer only runs once with
  // the mount-time `total`; if the consumer mounted us at total=0
  // (because the manifest hasn't resolved yet) and total later grows,
  // the initializer never reruns and visibleCount would stay at 0
  // forever — the user would see an empty feed after the loading state
  // disappears. Bump visibleCount up to INITIAL_VISIBLE the first time
  // `total` crosses past visibleCount from below. The `visibleCount === 0`
  // guard limits this to the "uninitialized" case — once the user has
  // loaded any chunks (visibleCount > 0), normal intersect/loadMore
  // growth takes over.
  useEffect(() => {
    if (visibleCount === 0 && total > 0) {
      setVisibleCount(Math.min(INITIAL_VISIBLE, total));
    }
  }, [total, visibleCount]);

  // Clamp visibleCount on list shrink (a Hide can drop entries below
  // visibleCount). Cheap to run every render.
  const effectiveVisible = Math.min(visibleCount, total);
  const exhausted = effectiveVisible >= total;
  // Place the sentinel TRIGGER_OFFSET back from the visible tail. Hide
  // the trigger when there aren't enough visible items to safely place
  // it (visibleCount ≤ TRIGGER_OFFSET) — a sentinel at index 0 fires on
  // the very first card, which is "always intersecting" and would
  // immediately exhaust the list. Consumers reach `loadMore()` instead.
  const triggerIndex =
    exhausted || effectiveVisible <= TRIGGER_OFFSET
      ? -1
      : effectiveVisible - TRIGGER_OFFSET - 1;

  // Schedule a chunk bump on the next animation frame. Safe to call
  // multiple times — only the first wins until the frame fires. Pure
  // function; `total` is captured by ref-less closure (the dep array
  // on the effect that consumes this handle gets re-bound when `total`
  // changes anyway).
  const queueBump = useCallback(
    (cap: number) => {
      if (pendingFrameRef.current !== null) return;
      setLoadingMore(true);
      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null;
        setVisibleCount((cur) => Math.min(cur + CHUNK_SIZE, cap));
        setLoadingMore(false);
      });
    },
    [],
  );

  const loadMore = useCallback(() => {
    if (effectiveVisible >= total) return;
    queueBump(total);
  }, [effectiveVisible, total, queueBump]);

  useEffect(() => {
    if (exhausted) return;
    const el = triggerRef.current;
    if (!el) return;
    // Some test/SSR environments lack IntersectionObserver. Don't crash;
    // the consumer can fall back to `loadMore()` as a manual "Load more"
    // button in that case, but we never need it in real browsers.
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (records) => {
        if (records.some((r) => r.isIntersecting)) {
          queueBump(total);
        }
      },
      {
        // Bottom-only margin (top/right/bottom/left, CSS shorthand).
        // Top expansion would fire the sentinel from above the viewport
        // too, which is harmless under `isIntersecting` but doesn't
        // match the "approaching the bottom" intent in the surrounding
        // comment.
        rootMargin: "0px 0px 200px 0px",
        threshold: 0,
      },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [effectiveVisible, total, exhausted, queueBump]);

  // Cancel any pending rAF on unmount so the closed-over setState can't
  // fire post-unmount.
  useEffect(() => {
    return () => {
      if (pendingFrameRef.current !== null) {
        cancelAnimationFrame(pendingFrameRef.current);
        pendingFrameRef.current = null;
      }
    };
  }, []);

  return {
    visibleCount: effectiveVisible,
    triggerRef,
    triggerIndex,
    exhausted,
    loadMore,
    loadingMore,
  };
}
